<template>
  <OmtLayoutContainer
    bg="#F6F6F9"
    :minHeight="['initial', 'calc(100vh - 80px)']"
  >
    <OmtRow>
      <CBox
        v-if="currentStep !== 'success'"
        width="100%"
        mt="66px"
      >
        <CFlex
          flexDirection="column"
          width="100%"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          py="40px"
          bg="#FFF"
          borderRadius="4px"
        >
          <CFlex
            width="84px"
            height="84px"
            borderRadius="50%"
            border="4px solid"
            :borderColor="stepConfig.colorHex"
            alignItems="center"
            justifyContent="center"
            mb="32px"
            class="step-icon-container"
          >
            <img
              :src="logoOmintSrc"
              alt="Logo Omint"
              width="48px"
              height="54px"
            />
          </CFlex>

          <OmtTypography
            as="h1-bold"
            color="var(--colors-light-primary-dark-blue)"
            mb="8px"
          >
            {{ stepConfig.title }}
          </OmtTypography>

          <OmtTypography
            as="p1"
            color="var(--colors-light-primary-gray2)"
            fontSize="14px"
            lineHeight="20px"
          >
            {{ stepConfig.subtitle }}
          </OmtTypography>
        </CFlex>

        <CFlex
          justifyContent="center"
          width="100%"
          mt="70px"
        >
          <CBox
            width="100%"
            height="8px"
            bg="#DADADA"
            borderRadius="4px"
            overflow="hidden"
            position="relative"
          >
            <CBox
              height="100%"
              borderRadius="4px"
              :bg="stepConfig.colorHex"
              :width="`${progressPercent}%`"
              class="step-progress-bar"
            />
          </CBox>
        </CFlex>
      </CBox>

      <!-- Estado Concluído: Resultado com Risco e Orientação -->
      <CFlex
        v-else
        flexDirection="column"
        width="100%"
        justify="space-between"
        minHeight="calc(100vh - 140px)"
        mt="16px"
      >
        <CBox width="100%">
          <OmtTypography
            as="h1-large"
            color="var(--colors-light-primary-dark-blue)"
            mb="40px"
            lineHeight="28px"
          >
            Análise concluída com sucesso
          </OmtTypography>

          <!-- Card de Risco -->
          <CBox
            bg="#EAF8F3"
            border="1px solid #00A979"
            borderRadius="8px"
            py="16px"
            px="20px"
            mb="16px"
          >
            <OmtTypography
              as="h1-bold"
              color="var(--colors-light-primary-dark-blue)"
              fontSize="26px"
              lineHeight="32px"
              mb="24px"
            >
              {{ displayRiskLevelTitle }}
            </OmtTypography>
            <OmtTypography
              as="p1"
              color="var(--colors-light-primary-gray1)"
            >
              {{ displayRiskDescription }}
            </OmtTypography>
          </CBox>

          <!-- Card de Orientação -->
          <CBox
            bg="#FFFFFF"
            border="1px solid #DADADA"
            borderRadius="8px"
            p="20px"
            mb="36px"
          >
            <OmtTypography
              as="h2-bold"
              color="var(--colors-light-primary-navy-blue)"
              mb="14px"
            >
              {{ orientationTitle }}
            </OmtTypography>
            <OmtTypography
              as="p1"
              color="#4E4E4E"
              fontSize="14px"
              lineHeight="20px"
            >
              {{ displayOrientationDescription }}
            </OmtTypography>
          </CBox>

          <!-- Aviso / Disclaimer -->
          <OmtTypography
            as="p0"
            color="var(--colors-light-support-error)"
          >
            Não substitui consulta médica.
          </OmtTypography>
        </CBox>

        <!-- Botões de Ação -->
        <CBox
          width="100%"
          mt="auto"
          pb="16px"
        >
          <OmtButton
            variant="outline"
            size="lg"
            width="100%"
            mb="16px"
            borderRadius="95px"
            @click="handleNovaConsulta"
          >
            Nova consulta
          </OmtButton>

          <OmtButton
            size="lg"
            width="100%"
            borderRadius="95px"
            @click="handleAtendimento"
          >
            Atendimento
          </OmtButton>
        </CBox>
      </CFlex>
    </OmtRow>
    <SentRequestModal
      :isOpen="modalSuccess"
      flowType="skinive"
      :protocol="resultado.protocoloANS"
      @close="voltarParaLista"
      @save="concluirAvaliacao"
    />
  </OmtLayoutContainer>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { CFlex, CBox } from "@chakra-ui/vue";
import { isMobile } from "@/utils/utils";
import SentRequestModal from "@/components/Organisms/Requests/SentRequestModal.vue";

const STEPS = {
  verifying: {
    key: "verifying",
    title: "Verificando imagem",
    subtitle: "Aguarde um momento...",
    colorHex: "#1CA0FF"
  },
  analyzing: {
    key: "analyzing",
    title: "Fazendo análise",
    subtitle: "Aguarde um momento...",
    colorHex: "#A08E60"
  },
  success: {
    key: "success",
    title: "Análise concluída com sucesso",
    subtitle: "Sua imagem foi avaliada com sucesso.",
    colorHex: "#00A979"
  }
};

export default {
  name: "skiniver-result",
  components: {
    CFlex,
    CBox,
    SentRequestModal
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (!vm.imageUrl && !vm.hasValidResult) {
        vm.$router.replace({ name: "skiniver.inicio" });
      }
    });
  },
  data() {
    return {
      currentStep: "verifying",
      progressPercent: 5,
      modalSuccess: false,
      timers: [],
      progressInterval: null
    };
  },
  computed: {
    ...mapState("skiniver", ["imageUrl", "uploadMethod", "resultado", "hasImageError", "errorMessage"]),
    hasValidResult() {
      return Boolean(this.resultado?.riskLevelTitle || this.resultado?.risk || this.resultado?.id);
    },
    baseUrl() {
      return process.env.VUE_APP_BASE_URL || "/";
    },
    logoOmintSrc() {
      return `${this.baseUrl}images/omint/logo_omint.svg`;
    },
    stepConfig() {
      return STEPS[this.currentStep] || STEPS.verifying;
    },
    displayRiskLevelTitle() {
      return this.resultado?.riskLevelTitle || "Risco baixo";
    },
    displayRiskDescription() {
      return this.resultado?.riskDescription || "Sem alerta crítico na imagem analisada.";
    },
    orientationTitle() {
      return "Orientação";
    },
    displayOrientationDescription() {
      return (
        this.resultado?.riskSuggestion ||
        "Acompanhe a evolução. Procure dermatologista se notar mudança, dor, coceira ou crescimento."
      );
    }
  },
  mounted() {
    this.$root.$emit("hide-loading");
    if (this.imageUrl) {
      this.runAnalysisFlow();
    } else if (this.hasValidResult) {
      this.setStep("success");
      this.progressPercent = 100;
    } else {
      this.$router.replace({ name: "skiniver.inicio" });
    }
  },
  beforeDestroy() {
    this.clearAllTimers();
  },
  methods: {
    ...mapActions("skiniver", ["analyzeSkiniverImage"]),
    ...mapActions("ticket", ["saveExpRating"]),
    ...mapMutations("skiniver", {
      clearError: "CLEAR_ERROR",
      resetContext: "RESET_CONTEXT"
    }),
    setStep(step) {
      if (STEPS[step]) {
        this.currentStep = step;
      }
    },
    clearAllTimers() {
      if (this.timers && this.timers.length) {
        this.timers.forEach(t => clearTimeout(t));
        this.timers = [];
      }
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    },
    startProgressAnimation() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval);
      }
      this.progressPercent = 5;

      this.progressInterval = setInterval(() => {
        if (this.progressPercent < 45) {
          // Fase 1: Verificando imagem (avança até 45%)
          this.progressPercent = Math.min(45, this.progressPercent + 1.4);
          if (this.progressPercent >= 45 && this.currentStep === "verifying") {
            this.setStep("analyzing");
          }
        } else if (this.progressPercent < 85) {
          // Fase 2: Fazendo análise (avança até 85%)
          this.progressPercent = Math.min(85, this.progressPercent + 0.8);
        } else if (this.progressPercent < 90) {
          // Fase 3: Avanço gradual mais lento até 90%
          this.progressPercent = Math.min(90, this.progressPercent + 0.25);
        } else if (this.progressPercent < 95) {
          // Fase 4: avanço lento enquanto aguarda a resposta da API
          this.progressPercent = Math.min(95, this.progressPercent + 0.08);
        }
      }, 70);
    },
    async runAnalysisFlow() {
      this.clearAllTimers();
      this.setStep("verifying");
      this.startProgressAnimation();

      try {
        await this.analyzeSkiniverImage({ urlImagem: this.imageUrl });

        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
        this.progressPercent = 100;

        const successTimer = setTimeout(() => {
          if (this.resultado?.protocoloANS) {
            this.modalSuccess = true;
          } else {
            this.setStep("success");
          }
        }, 650);
        this.timers.push(successTimer);
      } catch (error) {
        console.error("Erro na análise da imagem:", error);

        if (this.progressInterval) {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
        }
        this.progressPercent = 100;

        const errorTimer = setTimeout(() => {
          if (this.uploadMethod === "camera" && isMobile()) {
            this.$router.push({ name: "skiniver.instrucoes" });
          } else {
            this.$router.push({ name: "skiniver.inicio" });
          }
        }, 650);
        this.timers.push(errorTimer);
      }
    },
    voltarParaLista() {
      this.modalSuccess = false;
      this.clearAllTimers();
      this.resetContext();
      this.$router.replace({ name: "skiniver.lista" });
    },
    async concluirAvaliacao({ rating, obs }) {
      this.$root.$emit("show-loading");
      try {
        await this.saveExpRating({
          ticket: this.resultado.protocoloANS,
          type: "9",
          step: "S",
          rating,
          obs
        });
      } catch (error) {
        console.error("Erro ao salvar a avaliação:", error);
      } finally {
        this.$root.$emit("hide-loading");
        this.voltarParaLista();
      }
    },
    handleNovaConsulta() {
      this.clearAllTimers();
      this.resetContext();
      this.$router.push({ name: "skiniver.inicio" });
    },
    handleAtendimento() {
      this.clearAllTimers();
      const pergunta = "Quero dermatologista perto de mim";
      this.$router
        .push({
          name: "redecredenciada-prototipo.chat",
          query: { pergunta }
        })
        .catch(() => {
          this.$router
            .push({
              path: "/redecredenciada-prototipo/chat",
              query: { pergunta }
            })
            .catch(() => {});
        });
    }
  }
};
</script>

<style scoped>
.step-icon-container {
  transition: border-color 0.4s ease;
}

.step-progress-bar {
  transition: width 0.45s ease-out, background-color 0.4s ease;
}
</style>
